import { RootState } from "common/reducers";
import { TaskRecord, TaskStatus } from "common/records/Task";
import { Map as ImmutableMap } from "immutable";

/**
 * Loads byId key for tasks
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {Map<number, TaskRecord>} Map<number, TaskRecord>
 */
export const byId = (state: RootState) => state.getIn(["tasks", "byId"]);

/**
 * Loads the task using taskId
 *
 * @param {RootState} state The RootState
 * @param {{taskId: string}} props
 * @returns {TaskRecord} TaskRecord
 */
export const task = (state: RootState, props: any) => byId(state).get(props.taskId);

/**
 * Returns the total number of tasks
 *
 * @param {RootState} state The RootState
 * @returns {number} The number of tasks
 */
export const count = (state: RootState): number => byId(state).size;

/**
 * Loads the streaming logs for a single task by id
 *
 * @param {RootState} state The RootState
 * @param {{ taskId: string | null }} props the task id (null returns undefined)
 * @returns {string | undefined} the task's `logs` string, or undefined if no task is selected
 */
export const taskLogs = (state: RootState, props: { taskId: string | null }): string | undefined => {
  if (!props.taskId) return undefined;
  return byId(state).getIn([props.taskId, "logs"]) as string | undefined;
};

/**
 * Loads the current status for a single task by id
 *
 * @param {RootState} state The RootState
 * @param {{ taskId: string | null }} props the task id (null returns undefined)
 * @returns {TaskStatus | undefined} the task's status, or undefined if no task is selected
 */
export const taskStatus = (state: RootState, props: { taskId: string | null }): TaskStatus | undefined => {
  if (!props.taskId) return undefined;
  return byId(state).getIn([props.taskId, "status"]) as TaskStatus | undefined;
};

/**
 * Finds an in-flight task for a given model + payload key.
 *
 * Used by integration modals (e.g. Hover Import) to detect a still-processing task on mount and
 * resume the import-in-progress UI rather than starting a new browse session.
 *
 * @param {RootState} state The RootState
 * @param {{ model: string; jobId: number }} props the task context model + the OCC job id stored in
 *   `context.action_payload.job_id`
 * @returns {TaskRecord | undefined} the first matching task with status `submitted` or `processing`
 */
export const activeTaskByModel = (
  state: RootState,
  props: { model: string; jobId: number },
): TaskRecord | undefined => {
  const tasks = byId(state) as ImmutableMap<string, TaskRecord> | undefined;
  if (!tasks) return undefined;
  return tasks.toList().find((t) => {
    const model = t.getIn(["context", "model"]);
    const taskJobId = t.getIn(["context", "action_payload", "job_id"]);
    const status = t.get("status");
    return model === props.model && taskJobId === props.jobId && (status === "submitted" || status === "processing");
  });
};
